import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Col, Image, Row } from 'react-bootstrap';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as z from 'zod';
import logo from '../../../assets/Logo.png';
import login_bg from '../../../assets/login_graphic.svg';
import useAPI from '../../../hooks/useAPI';
import Loader from '../../common/Loader';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import styles from './style.module.scss';
import { setCookies } from '../../../utility/cookies';
import AuthContext from '../../../contexts/AuthContext';
import CashCorp from '../../../assets/mai.png';

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email'),
  otp: z
    .string()
    .min(4, 'OTP must be 4 digits')
    .max(4, 'OTP must be 4 digits')
    .regex(/^\d{4}$/, 'OTP must be a 4-digit number')
    .optional(),
});

const Login = () => {
  const navigate = useNavigate();
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isCodeSentTimeout, setIsCodeSentTimeout] = useState(false);
  const [isOTPResent, setIsOTPResent] = useState(false);
  const { setAccessToken } = useContext(AuthContext);
  // const [resendCount, setResendCount] = useState(() => {
  //   return Number(localStorage.getItem('resendCount')) || 0;
  // });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const { refetch: verifyOtp, isLoading: isVerifying } = useAPI({
    url: '/auth/verify/otp',
    method: 'POST',
    onSuccess: data => {
      toast.success(data.result.message);
      setCookies('accessToken', data?.result?.accessToken);
      setCookies('refreshToken', data?.result?.refreshToken);
      setCookies('user-shard', data?.result?.['user-shard']);
      setCookies('role', data?.result?.role);
      setAccessToken(data?.result?.accessToken);
      // window.location.href = '/wallet';
    },
    onError: error => {
      toast.error(
        (error?.response?.data as { customErrorNumber?: number })
          ?.customErrorNumber === 10000
          ? 'Incorrect OTP. Please try again.'
          : error?.response?.data?.message || 'An error occurred',
      );
    },
  });

  useEffect(() => {
    if (isCodeSent) {
      const timeout = setTimeout(() => {
        setIsCodeSentTimeout(false);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [isCodeSentTimeout]);

  const { refetch: sendOtp, isLoading } = useAPI({
    url: '/auth/send/otp',
    method: 'POST',
    onSuccess: data => {
      toast.success(data.result.message);
      setIsCodeSent(true);
      setIsCodeSentTimeout(true);
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const onSubmit = async (data: FieldValues) => {
    const trimmedEmail = data?.email?.trim();
    if (!isCodeSent) {
      await sendOtp({
        email: trimmedEmail,
        phoneNumber: '',
        countrycode: '',
        type: 'EMAIL',
      });
    } else {
      await verifyOtp({
        email: trimmedEmail,
        otp: data?.otp,
        phoneNumber: '',
        type: 'EMAIL',
      });
    }
  };

  const handleResend = () => {
    // const newCount = resendCount + 1;
    // setResendCount(newCount);
    // localStorage.setItem('resendCount', newCount.toString());

    // if (newCount <= 3) {
    const payload = {
      email: watch('email')?.trim(),
      phoneNumber: '',
      countrycode: '',
      type: 'EMAIL',
    };
    sendOtp(payload);
    setIsOTPResent(true);
    // }
  };

  return (
    <div className={styles.login_wrapper}>
      <Row className="h-100 d-flex justify-content-between align-items-center p-0 m-0">
        <Col
          lg={6}
          style={{ height: '100%' }}
          className="d-xs-none d-lg-block px-0 m-0"
        >
          <div className={styles.background_image}>
            <h1 className="fw-bolder text-white">Your Mai Cash Wallet</h1>
            <h1 className="text-white">Earn & Spend Securely</h1>
            <img src={login_bg} alt="logo" width={'100%'} />
          </div>
        </Col>
        <Col xs={12} lg={6} className={`${styles.form_content} p-0 m-0`}>
          <div className={styles.login_content}>
            <h1 className="fw-bolder fs-32 text-center mb-3 p-3">
              {' '}
              <Image
                src={CashCorp}
                style={{ height: '120px', objectFit: 'contain' }}
              />
            </h1>

            <h4 className={styles.login_title}>Welcome Back</h4>
            <h6 className={styles.login_subtitle}>
              Enter your email to receive the One Time Passcode
            </h6>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Input
                label=""
                type="text"
                id="email"
                placeholder="Enter your email"
                mainClassName={styles.input_box}
                className={`${styles.input_field} ${
                  errors.email?.message ? styles.input_field_error : ''
                }`}
                {...register('email')}
              />
              {isCodeSent && (
                <>
                  <Input
                    label=""
                    type="text"
                    id="otp"
                    placeholder="Enter your OTP"
                    mainClassName={styles.input_box}
                    className={`${styles.input_field} ${
                      errors.otp?.message ? styles.input_field_error : ''
                    }`}
                    {...register('otp')}
                  />
                  <div className={styles.resend_code}>
                    Didn't get a code?{' '}
                    <span
                      style={{
                        color: '#685FD3',
                        cursor: 'pointer',
                      }}
                      onClick={handleResend}
                    >
                      {'Resend Code'}
                    </span>
                  </div>
                </>
              )}
              <div className={styles.login_button}>
                <Button
                  disabled={(isVerifying || isLoading) && !isOTPResent}
                  style={{
                    cursor: 'pointer',
                  }}
                  type="submit"
                  className={styles.submit_button}
                >
                  {(isLoading || isVerifying) && !isOTPResent && <Loader />}
                  {isCodeSent ? 'Verify OTP' : 'Send OTP'}
                </Button>
              </div>

              {isCodeSent && isCodeSentTimeout && (
                <div className={styles.code_sent_button}>
                  Code Sent! Please check your email
                </div>
              )}
              {(errors.email?.message || errors.otp?.message) && (
                <div
                  className={`${styles.code_sent_button_error} ${styles.error_message}`}
                >
                  {(errors.email?.message as string) ||
                    (errors.otp?.message as string)}
                </div>
              )}
            </form>
            {/* <div className={styles.login_footer}>
              <div className="mt-3">
                <p className={styles.powered_by}>Powered by</p>
                <img src={logo} alt="logo" width={138} height={17} />
              </div>
            </div> */}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
